// Import components
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { CardModule } from 'primeng/card';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';

// Import pipes
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';

// Import services
import { StorageService } from '@zaoblako/web/core/services/storage/storage.service';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';

// Import types
import {
  StorageFormDto,
  type StorageFormInterface
} from '@zaoblako/shared/modules/storage/dtos/storage/storage-form.dto';
import type { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import type { StorageFindInterface } from '@zaoblako/shared/modules/storage/dtos/storage/storage-find.dto';

// Import externals
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-storage-form',
  templateUrl: './storage-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    InputTextComponent,
    SpinnerComponent,
    CheckboxModule,
    ButtonComponent,
    JoinPipe
  ]
})
export class StorageFormComponent extends BaseFormComponent<StorageFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public storage = <StorageFormInterface>{};

  public loading$: Observable<boolean>;

  public dto = StorageFormDto;
  public form$: Observable<FormType>;

  public constructor(
    public readonly formStoreService: FormStoreService,
    private readonly storageService: StorageService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);

    this.storageService
      .findById<StorageFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, {
        withDeleted: true
      })
      .subscribe({
        next: (storage) => {
          this.storage = storage;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveStorage(): void {
    if (this.checkValidate(this.storage).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.storageService.update<StorageFormInterface, StorageFindInterface>(this.storage).subscribe({
          next: (admin) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Хранилище S3 "${this.storage.name}" обновлено!`);
            this.formStoreService.saveNext(admin, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.storageService.create<StorageFindInterface>(this.storage).subscribe({
          next: (admin) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Хранилище S3 создано!');
            this.formStoreService.saveNext(admin, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteStorage(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить хранилище S3 "${this.storage.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.storageService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Хранилище S3 "${this.storage.name}" удалено!`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
